/**
 * Industry Cost Database Component
 * Regional construction cost lookup with BLS live labor data, city-level pricing, and search
 */

import React, { useState, useEffect, useCallback } from 'react';
import { 
  MapPin, DollarSign, Search, TrendingUp, Building2, 
  Truck, Shovel, Leaf, HardHat, Package, ChevronDown,
  Plus, Download, RefreshCw, Wrench, Droplets, Hammer,
  Zap, Users, Activity
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Separator } from './ui/separator';
import { toast } from 'sonner';

const CATEGORY_ICONS = {
  demolition: Building2,
  excavation: Shovel,
  trucking: Truck,
  import_fill: Package,
  civil: HardHat,
  environmental: Leaf,
  labor: Users,
  equipment: Truck,
  utilities: Droplets,
  concrete: Hammer,
  site_work: Wrench
};

const CATEGORY_COLORS = {
  demolition: 'text-red-500',
  excavation: 'text-amber-600',
  trucking: 'text-blue-500',
  import_fill: 'text-green-600',
  civil: 'text-purple-500',
  environmental: 'text-emerald-500',
  labor: 'text-orange-500',
  equipment: 'text-slate-600',
  utilities: 'text-cyan-500',
  concrete: 'text-gray-600',
  site_work: 'text-lime-600'
};

const RSMeansDatabase = ({ onAddToEstimate }) => {
  const [regions, setRegions] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState('midwest');
  const [selectedCity, setSelectedCity] = useState('');
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('excavation');
  const [costData, setCostData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [activeTab, setActiveTab] = useState('browse');
  const [blsConnected, setBlsConnected] = useState(false);
  const [dbStats, setDbStats] = useState(null);

  // Load database stats on mount
  useEffect(() => {
    const loadStats = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/rsmeans/stats`);
        if (response.ok) {
          const data = await response.json();
          setDbStats(data);
          setBlsConnected(data.bls_connected);
        }
      } catch (error) {
        console.error('Failed to load stats:', error);
      }
    };
    loadStats();
  }, []);

  // Load regions on mount
  useEffect(() => {
    const loadRegions = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/rsmeans/regions`);
        if (response.ok) {
          const data = await response.json();
          setRegions(data.regions);
        }
      } catch (error) {
        console.error('Failed to load regions:', error);
      }
    };
    loadRegions();
  }, []);

  // Load cities on mount
  useEffect(() => {
    const loadCities = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/rsmeans/cities`);
        if (response.ok) {
          const data = await response.json();
          setCities(data.cities);
        }
      } catch (error) {
        console.error('Failed to load cities:', error);
      }
    };
    loadCities();
  }, []);

  // Load categories on mount
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/rsmeans/categories`);
        if (response.ok) {
          const data = await response.json();
          setCategories(data.categories);
        }
      } catch (error) {
        console.error('Failed to load categories:', error);
      }
    };
    loadCategories();
  }, []);

  // Load cost data when region, city, or category changes
  useEffect(() => {
    const loadCosts = async () => {
      if (!selectedRegion || !selectedCategory) return;
      
      setLoading(true);
      try {
        let url = `${process.env.REACT_APP_BACKEND_URL}/api/rsmeans/costs/${selectedCategory}?region=${selectedRegion}`;
        if (selectedCity) {
          url += `&city=${selectedCity}`;
        }
        
        const response = await fetch(url);
        if (response.ok) {
          const data = await response.json();
          setCostData(data);
        }
      } catch (error) {
        console.error('Failed to load costs:', error);
        toast.error('Failed to load cost data');
      } finally {
        setLoading(false);
      }
    };
    loadCosts();
  }, [selectedRegion, selectedCity, selectedCategory]);

  // Search function
  const handleSearch = useCallback(async () => {
    if (!searchQuery || searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }
    
    setSearching(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/rsmeans/search?q=${encodeURIComponent(searchQuery)}&region=${selectedRegion}`
      );
      if (response.ok) {
        const data = await response.json();
        setSearchResults(data.results);
      }
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setSearching(false);
    }
  }, [searchQuery, selectedRegion]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (activeTab === 'search') {
        handleSearch();
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, activeTab, handleSearch]);

  const selectedRegionData = regions.find(r => r.id === selectedRegion);
  const selectedCityData = cities.find(c => c.id === selectedCity);
  const CategoryIcon = CATEGORY_ICONS[selectedCategory] || DollarSign;

  const formatCurrency = (num) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(num || 0);
  };

  // Get filtered cities for selected region
  const filteredCities = selectedRegion 
    ? cities.filter(c => c.region === selectedRegion)
    : cities;

  return (
    <div className="space-y-6 p-4">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <TrendingUp className="w-6 h-6 text-primary" />
            Industry Cost Database
          </h2>
          <p className="text-muted-foreground text-sm">
            {dbStats ? `${dbStats.total_items}+ cost items across ${dbStats.total_categories} categories • ${dbStats.total_cities} cities` : 'Regional construction cost data with live BLS labor rates'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {blsConnected ? (
            <Badge className="bg-green-600 hover:bg-green-700 text-xs flex items-center gap-1">
              <Activity className="w-3 h-3" />
              BLS Live Data
            </Badge>
          ) : (
            <Badge variant="secondary" className="text-xs">Industry Standard Rates</Badge>
          )}
          <Badge variant="outline" className="text-xs">Updated: {new Date().toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</Badge>
        </div>
      </div>

      {/* Data Sources Info */}
      {dbStats && (
        <Card className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 border-blue-500/20">
          <CardContent className="py-3">
            <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
              <div className="flex items-center gap-4">
                <span className="flex items-center gap-1 text-blue-600 dark:text-blue-400">
                  <Zap className="w-4 h-4" />
                  <span className="font-medium">Live Labor Data from Bureau of Labor Statistics</span>
                </span>
              </div>
              <div className="flex items-center gap-4 text-muted-foreground">
                <span>{dbStats.total_regions} regions</span>
                <span>•</span>
                <span>{dbStats.total_cities} cities</span>
                <span>•</span>
                <span>{dbStats.total_items} items</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="browse">Browse by Category</TabsTrigger>
          <TabsTrigger value="search">Search All Items</TabsTrigger>
        </TabsList>

        {/* Browse Tab */}
        <TabsContent value="browse" className="space-y-6">
          {/* Location Selection */}
          <Card>
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Region Selection */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    Region
                  </Label>
                  <Select value={selectedRegion} onValueChange={(v) => {
                    setSelectedRegion(v);
                    setSelectedCity(''); // Reset city when region changes
                  }}>
                    <SelectTrigger data-testid="region-select">
                      <SelectValue placeholder="Select a region" />
                    </SelectTrigger>
                    <SelectContent>
                      {regions.map((region) => (
                        <SelectItem key={region.id} value={region.id}>
                          {region.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* City Selection */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    City (Optional - More Precise)
                  </Label>
                  <Select value={selectedCity || "none"} onValueChange={(v) => setSelectedCity(v === "none" ? "" : v)}>
                    <SelectTrigger data-testid="city-select">
                      <SelectValue placeholder="Use regional average" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Use Regional Average</SelectItem>
                      {filteredCities.map((city) => (
                        <SelectItem key={city.id} value={city.id}>
                          {city.name} ({(city.index * 100).toFixed(0)}%)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Category Selection */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Package className="w-4 h-4" />
                    Cost Category
                  </Label>
                  <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                    <SelectTrigger data-testid="category-select">
                      <SelectValue placeholder="Select a category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => {
                        const Icon = CATEGORY_ICONS[cat] || Package;
                        return (
                          <SelectItem key={cat} value={cat}>
                            <span className="flex items-center gap-2">
                              <Icon className={`w-4 h-4 ${CATEGORY_COLORS[cat] || ''}`} />
                              {cat.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            </span>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Regional/City Factors */}
          {(selectedRegionData || selectedCityData) && (
            <Card className="bg-primary/5">
              <CardContent className="pt-6">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  {selectedCityData ? selectedCityData.name : selectedRegionData?.name} Cost Factors
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-3 bg-background rounded-lg">
                    <div className="text-2xl font-bold text-primary">
                      {selectedCityData 
                        ? `${(selectedCityData.index * 100).toFixed(0)}%`
                        : `${(selectedRegionData?.factors.material * 100).toFixed(0)}%`
                      }
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {selectedCityData ? 'City Index' : 'Material'}
                    </div>
                  </div>
                  {!selectedCityData && (
                    <>
                      <div className="text-center p-3 bg-background rounded-lg">
                        <div className="text-2xl font-bold text-blue-500">
                          {(selectedRegionData?.factors.labor * 100).toFixed(0)}%
                        </div>
                        <div className="text-xs text-muted-foreground">Labor</div>
                      </div>
                      <div className="text-center p-3 bg-background rounded-lg">
                        <div className="text-2xl font-bold text-amber-500">
                          {(selectedRegionData?.factors.equipment * 100).toFixed(0)}%
                        </div>
                        <div className="text-xs text-muted-foreground">Equipment</div>
                      </div>
                    </>
                  )}
                  <div className="text-center p-3 bg-background rounded-lg">
                    <div className="text-2xl font-bold text-green-500">
                      {selectedCityData 
                        ? `${(selectedCityData.index * 100).toFixed(0)}%`
                        : `${(selectedRegionData?.factors.overall * 100).toFixed(0)}%`
                      }
                    </div>
                    <div className="text-xs text-muted-foreground">Overall</div>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-4 text-center">
                  100% = National Average | &gt;100% = Higher than average | &lt;100% = Lower than average
                </p>
              </CardContent>
            </Card>
          )}

          {/* Cost Data Table */}
          {costData && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CategoryIcon className={`w-5 h-5 ${CATEGORY_COLORS[selectedCategory] || ''}`} />
                  {selectedCategory.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} Costs
                  <Badge variant="outline" className="ml-2">{costData.items.length} items</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {/* Table Header */}
                  <div className="grid grid-cols-12 gap-2 py-2 px-3 bg-muted rounded-t-lg font-medium text-sm">
                    <div className="col-span-5">Description</div>
                    <div className="col-span-2 text-center">Unit</div>
                    <div className="col-span-2 text-right">Base Cost</div>
                    <div className="col-span-3 text-right">Regional Cost</div>
                  </div>
                  
                  {/* Table Rows */}
                  {costData.items.map((item, index) => (
                    <div 
                      key={item.item_code}
                      className={`grid grid-cols-12 gap-2 py-3 px-3 ${index % 2 === 0 ? 'bg-muted/30' : ''} rounded-lg hover:bg-muted/50 transition-colors group`}
                    >
                      <div className="col-span-5">
                        <div className="font-medium flex items-center gap-2">
                          {item.description}
                          {item.data_source === 'BLS Live' && (
                            <Badge className="bg-green-600 hover:bg-green-700 text-[10px] px-1 py-0">
                              BLS
                            </Badge>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground">{item.item_code}</div>
                      </div>
                      <div className="col-span-2 text-center">
                        <Badge variant="outline">{item.unit}</Badge>
                      </div>
                      <div className="col-span-2 text-right text-muted-foreground">
                        {formatCurrency(item.base_cost)}
                      </div>
                      <div className="col-span-3 text-right font-bold text-primary">
                        {formatCurrency(item.regional_cost)}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {loading && (
            <div className="text-center py-8 text-muted-foreground flex items-center justify-center gap-2">
              <RefreshCw className="w-5 h-5 animate-spin" />
              Loading cost data...
            </div>
          )}
        </TabsContent>

        {/* Search Tab */}
        <TabsContent value="search" className="space-y-6">
          <Card>
            <CardContent className="pt-6">
              <div className="flex gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search for cost items (e.g., 'excavator', 'concrete', 'pipe')..."
                    className="pl-9"
                    data-testid="cost-search-input"
                  />
                </div>
                <Select value={selectedRegion} onValueChange={setSelectedRegion}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="Region" />
                  </SelectTrigger>
                  <SelectContent>
                    {regions.map((region) => (
                      <SelectItem key={region.id} value={region.id}>
                        {region.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Search Results */}
          {searching ? (
            <div className="text-center py-8 text-muted-foreground flex items-center justify-center gap-2">
              <RefreshCw className="w-5 h-5 animate-spin" />
              Searching...
            </div>
          ) : searchResults.length > 0 ? (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Search className="w-5 h-5" />
                  Search Results
                  <Badge variant="outline">{searchResults.length} items found</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {searchResults.map((item, index) => {
                    const Icon = CATEGORY_ICONS[item.category] || Package;
                    return (
                      <div 
                        key={`${item.category}-${item.item_code}`}
                        className={`grid grid-cols-12 gap-2 py-3 px-3 ${index % 2 === 0 ? 'bg-muted/30' : ''} rounded-lg hover:bg-muted/50 transition-colors`}
                      >
                        <div className="col-span-5">
                          <div className="font-medium flex items-center gap-2">
                            <Icon className={`w-4 h-4 ${CATEGORY_COLORS[item.category] || ''}`} />
                            {item.description}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {item.category.replace(/_/g, ' ')} • {item.item_code}
                          </div>
                        </div>
                        <div className="col-span-2 text-center">
                          <Badge variant="outline">{item.unit}</Badge>
                        </div>
                        <div className="col-span-2 text-right text-muted-foreground">
                          {formatCurrency(item.base_cost)}
                        </div>
                        <div className="col-span-3 text-right font-bold text-primary">
                          {formatCurrency(item.regional_cost)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          ) : searchQuery.length >= 2 ? (
            <div className="text-center py-8 text-muted-foreground">
              No items found matching "{searchQuery}"
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              Enter at least 2 characters to search
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default RSMeansDatabase;
